import { useTranslation } from '@papan/i18n'
import { rootAccessBlocksSplit, type PartitionPreview, type PartitionMoveBlock } from '@papan/types'

/** The sentence for each move refusal the preview can know up front. */
const BLOCKED_KEY: Record<PartitionMoveBlock, string> = {
  destNotWritable: 'protectedBranches.blockedDestNotWritable',
  destUnavailable: 'protectedBranches.blockedDestUnavailable',
  nestedShared: 'protectedBranches.blockedNestedShared',
  accessChange: 'protectedBranches.blockedAccessChange',
  needsManage: 'protectedBranches.needsManage',
  wouldLoseAccess: 'protectedBranches.wouldLoseAccess',
  shellGovernance: 'protectedBranches.shellGovernance',
}

export interface ProtectedBranchesPreviewProps {
  op: 'delete' | 'move'
  preview: PartitionPreview
}

/**
 * The `message` of a `useConfirm()` dialog for a delete/move that hits
 * protected branches (#801) — what will be skipped (with a reason, or just a
 * count once the surface stops listing it), what's left to act on, and — for
 * a move — what the root's own access setup leaves behind on the split-off
 * shell.
 *
 * `entityRoot` refuses the whole split and renders alone (nothing else in
 * the preview is relevant once the root itself can't be split). Likewise, a
 * root that owns its own access but whose mover can't manage it
 * (`rootAccessBlocksSplit`, the ONE reading of it) refuses the whole move —
 * this is the one refusal a move's OWN preview can already see;
 * `needsManage`'s ACT-time twin and `wouldLoseAccess` (which needs the
 * DESTINATION, never knowable from this preview) are thrown by the storage
 * chokepoint instead and rendered from the caught error, not from here.
 *
 * Every list gets `max-h-64 overflow-y-auto` — a protected/removable set can
 * run into the hundreds and this is rendered inside a modal.
 */
export function ProtectedBranchesPreview({ op, preview }: ProtectedBranchesPreviewProps) {
  const { t } = useTranslation('files')
  const { protected: skipped, hiddenProtectedCount, removable, entityRoot, rootAccess } = preview

  // L4 round 6 (M-17c): the move to the chosen destination WILL be refused (the server ran
  // the move's own judgements) — say why, and nothing else (no list to act on).
  if (op === 'move' && preview.blocked) {
    return <p>{t(BLOCKED_KEY[preview.blocked])}</p>
  }

  if (entityRoot) {
    return <p>{t('protectedBranches.entityRootRefused')}</p>
  }

  if (op === 'move' && rootAccessBlocksSplit(rootAccess)) {
    return <p>{t('protectedBranches.needsManage')}</p>
  }

  const hasSkipped = skipped.length > 0 || hiddenProtectedCount > 0
  const nothingToAct = removable.length === 0

  return (
    <div className="space-y-4">
      {hasSkipped && (
        <div>
          <h4 className="font-medium mb-1">
            {t(op === 'move' ? 'protectedBranches.skippedHeadingMove' : 'protectedBranches.skippedHeading')}
          </h4>
          <ul className="max-h-64 overflow-y-auto text-sm space-y-0.5">
            {skipped.map(item => (
              <li key={item.id}>
                {item.name} — {t(item.reason === 'self' ? 'protectedBranches.reasonSelf' : 'protectedBranches.reasonDescendant')}
              </li>
            ))}
            {hiddenProtectedCount > 0 && (
              <li>{t('protectedBranches.hiddenCount', { count: hiddenProtectedCount })}</li>
            )}
          </ul>
        </div>
      )}

      {nothingToAct ? (
        <div>
          <h4 className="font-medium mb-1">{t('protectedBranches.nothingTitle')}</h4>
          <p>{t(op === 'move' ? 'protectedBranches.nothingMovable' : 'protectedBranches.nothingRemovable')}</p>
        </div>
      ) : (
        <div>
          <h4 className="font-medium mb-1">
            {t(op === 'move' ? 'protectedBranches.moveHeading' : 'protectedBranches.deleteHeading')}
          </h4>
          <ul className="max-h-64 overflow-y-auto text-sm space-y-0.5">
            {removable.map(item => <li key={item.id}>{item.name}</li>)}
          </ul>
        </div>
      )}

      {op === 'move' && !nothingToAct && rootAccess && (
        <div className="text-sm space-y-0.5">
          {(rootAccess.restricted || rootAccess.grantCount > 0) && <p>{t('protectedBranches.rootAccessCopied')}</p>}
          {rootAccess.linkCount > 0 && (
            <p>{t('protectedBranches.linksStay', { count: rootAccess.linkCount })}</p>
          )}
          {rootAccess.orgShareCount > 0 && (
            <p>{t('protectedBranches.orgSharesStay', { count: rootAccess.orgShareCount })}</p>
          )}
          {rootAccess.appGrantCount > 0 && (
            <p>{t('protectedBranches.appAccessStays', { count: rootAccess.appGrantCount })}</p>
          )}
        </div>
      )}
    </div>
  )
}
